/**
 * The record on screen, the last few viewed, and the one the attorney removed
 * from the composer. Pages write it through `useOnScreen`; the global panel
 * reads it for the chip and each turn's context.
 */
import { create } from "zustand";

import {
  onScreenKey,
  pushRecent,
  type OnScreen,
  type RecentOnScreen
} from "./onScreen";

type State = {
  current: OnScreen | null;
  recent: RecentOnScreen[];
  /** Removed from the composer; sent again once the page shows another record. */
  dismissedKey: string | null;
};

type Actions = {
  publish: (entry: OnScreen) => void;
  /** The page that published `key` stopped showing it. */
  clear: (key: string) => void;
  dismiss: (key: string) => void;
};

export const useOnScreenStore = create<State & Actions>()((set) => ({
  current: null,
  recent: [],
  dismissedKey: null,
  publish: (entry) =>
    set((s) => {
      const key = onScreenKey(entry);
      return {
        current: entry,
        recent: pushRecent(s.recent, entry),
        dismissedKey: s.dismissedKey === key ? s.dismissedKey : null
      };
    }),
  clear: (key) =>
    set((s) =>
      s.current && onScreenKey(s.current) === key ? { current: null } : s
    ),
  dismiss: (key) => set({ dismissedKey: key })
}));

/** Imperative access for code outside React (the turn's selection adapter). */
export const onScreenStore = {
  get: (): State => {
    const { current, recent, dismissedKey } = useOnScreenStore.getState();
    return { current, recent, dismissedKey };
  },
  dismiss: (key: string) => useOnScreenStore.getState().dismiss(key),
  reset: () =>
    useOnScreenStore.setState({ current: null, recent: [], dismissedKey: null })
};
